// AI Efficiency (#1) — the honest AI-vs-non-AI comparison. Adoption % tells us how
// much AI is used; this tells us whether AI work is actually faster and no buggier.
// Cycle time + throughput/dev come from /api/pulse/ai-efficiency (closed sprints,
// recombined across pods). Defect-injection rate is joined from the AI-quality
// snapshot, and QA-fail volume from the quality endpoint.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { PULSE_COLORS } from './pulseConfig.js'

const AI_COLOR = '#a78bfa'
const NON_AI_COLOR = '#38bdf8'

// One paired metric: AI value beside non-AI value, with a gap badge whose color
// reflects whether AI is favourable for this metric (lower cycle time = good, etc.).
function PairCard({ label, sublabel, ai, nonAi, unit, betterWhen, fmt = (v) => v }) {
    const hasData = ai != null && nonAi != null
    const gap = hasData ? ai - nonAi : null
    const aiFavourable = hasData ? (betterWhen === 'lower' ? gap < 0 : gap > 0) : null
    const gapColor = aiFavourable == null ? PULSE_COLORS.textSubtle : aiFavourable ? PULSE_COLORS.success : PULSE_COLORS.danger
    const arrow = gap == null || gap === 0 ? '→' : gap < 0 ? '↓' : '↑'

    return (
        <div
            style={{
                background: PULSE_COLORS.bgInner,
                border: `1px solid ${PULSE_COLORS.border}`,
                borderRadius: '10px',
                padding: '16px',
                flex: '1 1 200px',
                minWidth: '200px',
            }}
        >
            <div style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '14px' }}>{label}</div>
            <div style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', marginBottom: '12px' }}>{sublabel}</div>
            {hasData ? (
                <>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
                        <div>
                            <div style={{ color: AI_COLOR, fontSize: '26px', fontWeight: 700 }}>
                                {fmt(ai)}
                                <span style={{ fontSize: '12px', fontWeight: 500 }}>{unit}</span>
                            </div>
                            <div style={{ color: AI_COLOR, fontSize: '11px' }}>AI-assisted</div>
                        </div>
                        <div>
                            <div style={{ color: NON_AI_COLOR, fontSize: '26px', fontWeight: 700 }}>
                                {fmt(nonAi)}
                                <span style={{ fontSize: '12px', fontWeight: 500 }}>{unit}</span>
                            </div>
                            <div style={{ color: NON_AI_COLOR, fontSize: '11px' }}>Traditional</div>
                        </div>
                    </div>
                    <div style={{ color: gapColor, fontSize: '12px', fontWeight: 600, marginTop: '10px' }}>
                        {arrow} {fmt(Math.abs(gap))}
                        {unit} {aiFavourable ? 'better with AI' : aiFavourable === false ? 'worse with AI' : ''}
                    </div>
                </>
            ) : (
                <div style={{ color: PULSE_COLORS.textSubtle, fontSize: '13px', padding: '12px 0' }}>
                    Not enough data
                </div>
            )}
        </div>
    )
}

export default function AiEfficiency({ refreshSignal }) {
    const [eff, setEff] = useState(null)
    const [defect, setDefect] = useState(null)
    const [rework, setRework] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const [effRes, snapRes, qualRes] = await Promise.all([
                fetch('/api/pulse/ai-efficiency?sprints=10'),
                fetch('/api/pulse/ai-analysis/snapshots?limit=1'),
                fetch('/api/pulse/quality?timeRange=6m'),
            ])
            const effJson = await effRes.json()
            if (!effRes.ok) throw new Error(effJson?.error ?? `ai-efficiency ${effRes.status}`)
            setEff(effJson)

            const snap = (await snapRes.json())?.snapshots?.[0]
            if (snap && snap.ai_rate != null) {
                // Stored as a 0..1 fraction; show as defects per 100 stories.
                setDefect({ ai: snap.ai_rate * 100, nonAi: snap.non_ai_rate * 100 })
            }
            const qaFailed = (await qualRes.json())?.defects?.qaFailed
            if (qaFailed && qaFailed.withAiCompleted != null) {
                setRework({ ai: qaFailed.withAiCompleted, nonAi: qaFailed.withoutAiCompleted })
            }
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load, refreshSignal])

    const cycle = eff?.summary?.cycleTime
    const tput = eff?.summary?.throughputPerDev

    const chartData = useMemo(
        () =>
            (eff?.series ?? []).map((s) => ({
                label: `S${s.sprintNumber}`,
                'AI cycle (d)': s.cycleTimeAi,
                'Traditional cycle (d)': s.cycleTimeNonAi,
            })),
        [eff]
    )

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
                marginTop: '24px',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px', margin: 0 }}>
                        ⚡ AI Efficiency — is AI work actually faster & no buggier?
                    </h3>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '6px 0 0' }}>
                        Latest closed sprint, all pods. Adoption % tells you usage; this tells you payoff.
                    </p>
                </div>
                {loading && <span style={{ color: PULSE_COLORS.textMuted, fontSize: '12px' }}>Loading…</span>}
            </div>

            {error && (
                <div style={{ color: PULSE_COLORS.danger, fontSize: '13px', marginBottom: '12px' }}>⚠ {error}</div>
            )}

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <PairCard
                    label="Cycle time"
                    sublabel="work-start → done, avg days (lower is better)"
                    ai={cycle?.ai}
                    nonAi={cycle?.nonAi}
                    unit="d"
                    betterWhen="lower"
                    fmt={(v) => (Math.round(v * 10) / 10).toFixed(1)}
                />
                <PairCard
                    label="Throughput / dev"
                    sublabel="completed points per active dev (higher is better)"
                    ai={tput?.ai}
                    nonAi={tput?.nonAi}
                    unit=""
                    betterWhen="higher"
                    fmt={(v) => (Math.round(v * 10) / 10).toFixed(1)}
                />
                <PairCard
                    label="Defect injection"
                    sublabel="defects per 100 stories, 180d (lower is better)"
                    ai={defect?.ai}
                    nonAi={defect?.nonAi}
                    unit=""
                    betterWhen="lower"
                    fmt={(v) => (Math.round(v * 10) / 10).toFixed(1)}
                />
                <PairCard
                    label="QA-fail volume"
                    sublabel="stories bounced from QA, 6mo (lower is better)"
                    ai={rework?.ai}
                    nonAi={rework?.nonAi}
                    unit=""
                    betterWhen="lower"
                    fmt={(v) => Math.round(v)}
                />
            </div>

            {chartData.length > 0 && (
                <div>
                    <div style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', marginBottom: '8px' }}>
                        Cycle time trend — AI vs traditional, by sprint
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={PULSE_COLORS.border} />
                            <XAxis dataKey="label" tick={{ fill: PULSE_COLORS.textMuted, fontSize: 11 }} />
                            <YAxis tick={{ fill: PULSE_COLORS.textMuted, fontSize: 11 }} />
                            <Tooltip
                                contentStyle={{
                                    background: PULSE_COLORS.bgInner,
                                    border: `1px solid ${PULSE_COLORS.border}`,
                                    borderRadius: '8px',
                                    color: PULSE_COLORS.text,
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: '12px' }} />
                            <Line type="monotone" dataKey="AI cycle (d)" stroke={AI_COLOR} strokeWidth={2} connectNulls />
                            <Line
                                type="monotone"
                                dataKey="Traditional cycle (d)"
                                stroke={NON_AI_COLOR}
                                strokeWidth={2}
                                connectNulls
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    )
}
